import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { ChevronLeft, CreditCard, Shield, Lock, Check, Smartphone } from "lucide-react";
import { toast } from "react-toastify";
import { useAppSelector } from "../../store/hooks";
import conferenceAndTradeMissionService from "../../api/ConferenceAndTradeMission";
import {
  loadPayLaterSessionForSlug,
  savePayLaterSession,
} from "../../utils/conferencePayLaterSession";

export default function ConferencePayment() {
  const navigate = useNavigate();
  const location = useLocation();
  const { slug } = useParams();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const fromLocation = location.state || {};
  const fromRegistrations = fromLocation.fromRegistrations === true;

  // Hold in state so we can restore from session when location.state is lost (e.g. refresh or back then Pay now again)
  const [registrationData, setRegistrationData] = useState(fromLocation.registrationData ?? null);
  const [eventData, setEventData] = useState(fromLocation.eventData ?? null);
  const [payLaterMeta, setPayLaterMeta] = useState({
    isGroup: fromLocation.isGroup,
    numberOfAttendees: fromLocation.numberOfAttendees,
    totalAmount: fromLocation.totalAmount,
    attendees: fromLocation.attendees,
    payLaterEntry: fromLocation.payLaterEntry,
  });
  const [eventDataLoading, setEventDataLoading] = useState(false);
  const hasFetchedEventDetails = useRef(false);
  const [paymentMethod, setPaymentMethod] = useState("momo");
  const [paymentData, setPaymentData] = useState({
    selectedTicket: fromLocation.selectedTicket || null,
    cardHolderName: "",
    cardNumber: "",
    expiryDate: "",
    cvv: "",
    momoProvider: "",
    momoPhone: "",
  });
  const [errors, setErrors] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);

  const { isGroup, numberOfAttendees, totalAmount, attendees: stateAttendees, payLaterEntry } = payLaterMeta;

  // Restore from session when location.state is empty (e.g. user clicked Pay later then Pay now again, or refresh)
  useEffect(() => {
    if (!isAuthenticated) {
      toast.info("Please sign in to continue");
      navigate("/signin");
      return;
    }
    if (!slug) return;

    const session = loadPayLaterSessionForSlug(slug);
    if (session) {
      console.log("[ConferencePayment] session data for slug:", slug, session);
    }
    const effectiveReg = registrationData ?? session?.registrationData;
    const effectiveEvent = eventData ?? session?.eventData;

    if (!effectiveReg || !effectiveEvent) {
      toast.error("Registration data not found. Please start from the event or your registrations.");
      navigate(`/event-details/${slug}`);
      return;
    }

    // Group registration: derive from registrationData/eventData when session has no top-level keys (e.g. session saved from API only).
    // Single registration restore will be handled separately when provided.
    const isGroupRegistration =
      effectiveReg?.registration_type === "group" || !!effectiveReg?.group_info;
    const regTotal = effectiveReg.total_amount ?? effectiveReg.totalAmount;
    const regNumAttendees = effectiveReg.group_info?.number_of_attendees ?? effectiveReg.number_of_attendees;
    const regAttendees = effectiveReg.attendees;
    const regTicketType = effectiveReg.ticket_type;
    const derivedNumberOfAttendees = isGroupRegistration
      ? (session?.numberOfAttendees ?? fromLocation.numberOfAttendees ?? regNumAttendees ?? (Array.isArray(regAttendees) ? regAttendees.length : undefined))
      : (session?.numberOfAttendees ?? fromLocation.numberOfAttendees);
    const derivedTotalAmount =
      session?.totalAmount ?? fromLocation.totalAmount ?? (isGroupRegistration ? regTotal : undefined) ?? undefined;
    const derivedAttendees = isGroupRegistration
      ? (session?.attendees ?? fromLocation.attendees ?? regAttendees ?? undefined)
      : (session?.attendees ?? fromLocation.attendees);
    const derivedSelectedTicket =
      session?.selectedTicket ??
      fromLocation.selectedTicket ??
      paymentData.selectedTicket ??
      (effectiveEvent?.ticket_types?.length && regTicketType
        ? effectiveEvent.ticket_types.find((t) => t.name === regTicketType?.name) ?? effectiveEvent.ticket_types.find((t) => parseFloat(t.price) === parseFloat(regTicketType?.price))
        : null) ??
      null;

    // Restore from session when state is missing or incomplete (e.g. Pay later → Pay now flow)
    const needsRestore =
      !registrationData ||
      !eventData ||
      (payLaterMeta.payLaterEntry && totalAmount == null && (session?.totalAmount != null || derivedTotalAmount != null)) ||
      (payLaterMeta.payLaterEntry && !paymentData.selectedTicket && (session?.selectedTicket || derivedSelectedTicket));

    if (needsRestore && (session || derivedTotalAmount != null || derivedSelectedTicket)) {
      if (!registrationData && session?.registrationData) setRegistrationData(session.registrationData);
      if (!eventData && session?.eventData) setEventData(session.eventData);
      setPayLaterMeta((prev) => ({
        ...prev,
        payLaterEntry: prev.payLaterEntry ?? fromLocation.payLaterEntry ?? true,
        isGroup: session?.isGroup ?? prev.isGroup ?? fromLocation.isGroup ?? isGroupRegistration,
        numberOfAttendees: derivedNumberOfAttendees ?? prev.numberOfAttendees ?? fromLocation.numberOfAttendees,
        totalAmount: derivedTotalAmount ?? prev.totalAmount ?? fromLocation.totalAmount,
        attendees: derivedAttendees ?? prev.attendees ?? fromLocation.attendees,
      }));
      if (!paymentData.selectedTicket && (session?.selectedTicket || derivedSelectedTicket)) {
        setPaymentData((prev) => ({ ...prev, selectedTicket: session?.selectedTicket ?? derivedSelectedTicket ?? prev.selectedTicket }));
      }
    }

    if (effectiveReg && effectiveEvent) {
      savePayLaterSession(slug, {
        registrationData: effectiveReg,
        eventData: effectiveEvent,
        isGroup: session?.isGroup ?? fromLocation.isGroup ?? isGroupRegistration,
        numberOfAttendees: derivedNumberOfAttendees ?? session?.numberOfAttendees ?? fromLocation.numberOfAttendees,
        totalAmount: derivedTotalAmount ?? session?.totalAmount ?? fromLocation.totalAmount,
        attendees: derivedAttendees ?? session?.attendees ?? fromLocation.attendees,
        selectedTicket: derivedSelectedTicket ?? fromLocation.selectedTicket ?? session?.selectedTicket ?? paymentData.selectedTicket,
      });
    }
  }, [isAuthenticated, slug]);

  // Pay-later entry: list API may not include ticket_types; fetch full event details once when missing.
  // API shape: { data: { status_code, message, reason, data: { event, similar_event }, point_in_time } }
  // Service returns res.data = response.data.data, so event is at res.data.data.event
  useEffect(() => {
    if (!slug || !eventData || hasFetchedEventDetails.current) return;
    if (eventData.ticket_types && eventData.ticket_types.length > 0) return;
    hasFetchedEventDetails.current = true;
    let cancelled = false;
    setEventDataLoading(true);
    conferenceAndTradeMissionService
      .getEventDetails(slug)
      .then((res) => {
        if (cancelled || !res?.data) return;
        const inner = res.data.data; // { event, similar_event }
        const eventObj = inner?.event ?? res.data?.event ?? res.data;
        if (!eventObj?.ticket_types?.length) return;
        setEventData((prev) => ({ ...prev, ...eventObj, ticket_types: eventObj.ticket_types }));
      })
      .catch(() => {
        if (!cancelled) toast.error("Could not load ticket options");
        hasFetchedEventDetails.current = false;
      })
      .finally(() => {
        if (!cancelled) setEventDataLoading(false);
      });
    return () => { cancelled = true; };
  }, [slug, eventData]);

  // For group bookings: when we have totalAmount + numberOfAttendees but no selectedTicket (e.g. restored session), infer ticket from unit price
  const hasInferredGroupTicket = useRef(false);
  useEffect(() => {
    if (!eventData?.ticket_types?.length || !isGroup || hasInferredGroupTicket.current) return;
    const num = numberOfAttendees ?? 0;
    const total = totalAmount;
    if (num <= 0 || total == null) return;
    if (paymentData.selectedTicket) return;
    const unitPrice = total / num;
    const ticket = eventData.ticket_types.find(
      (t) => Math.abs(parseFloat(t.price) - unitPrice) < 0.02
    ) || eventData.ticket_types.find((t) => parseFloat(t.price) > 0) || eventData.ticket_types[0];
    if (ticket) {
      hasInferredGroupTicket.current = true;
      setPaymentData((prev) => ({ ...prev, selectedTicket: ticket }));
    }
  }, [eventData, isGroup, numberOfAttendees, totalAmount, paymentData.selectedTicket]);

  const handlePayment = async (dataToUse = null) => {
    const data = dataToUse || paymentData;

    if (!data.selectedTicket) {
      toast.error("Please select a ticket");
      return;
    }

    setIsProcessing(true);
    try {
      const amount = isGroup && totalAmount != null ? totalAmount : (data.selectedTicket?.price ?? 0);
      const guests = stateAttendees ?? registrationData?.attendees ?? [];
      // Treat null/undefined registration_type as single (API may return null for single registrations)
      const isGroupRegistration =
        registrationData?.registration_type === "group" || !!registrationData?.group_info;
      const registrationType = isGroupRegistration ? "group" : "single";
      const numAttendeesForPayment = isGroupRegistration ? (numberOfAttendees ?? 1) : 1;
      const payload = {
        registration_id: registrationData?.slug || registrationData?.id || registrationData?.registration_id,
        event_slug: slug,
        ticket_name: data.selectedTicket?.name ?? data.selectedTicket,
        amount: Number(amount),
        payment_method: paymentMethod === "momo" ? "mobile_money" : "card",
        registration_type: registrationType,
        number_of_attendees: numAttendeesForPayment,
        ...(guests.length > 0 && { guests }),
      };

      // Always pass generic momo fields so API works; no mobile money form shown
      payload.momo_provider = paymentMethod === "momo" ? "mtn" : "card";
      payload.momo_phone = "0000000000";

      const response = await conferenceAndTradeMissionService.payConference(payload);

      if (response.status && (response.data?.status_code === 200 || response.data?.data)) {
        const outer = response.data?.data ?? response.data;
        const paymentLink =
          outer?.data?.api_pay_redirect_url ??
          outer?.api_pay_redirect_url ??
          outer?.payment_url ??
          outer?.payment_link ??
          outer?.link ??
          outer?.redirect_url ??
          outer?.checkout_url;

        if (paymentLink) {
          toast.success("Redirecting to payment...");
          window.location.href = paymentLink;
          return;
        }

        toast.success(response.message || "Payment initiated successfully.");
        navigate(`/event-details/${slug}/registration-success`, {
          state: {
            applicationId: slug,
            registrationId: registrationData?.id || registrationData?.registration_id,
            registrationData: registrationData,
            paymentVerified: false,
          },
        });
      } else {
        toast.error(response.message || "Payment failed. Please try again.");
        setIsProcessing(false);
      }
    } catch (error) {
      toast.error(error?.message || "Payment processing failed. Please try again.");
      setIsProcessing(false);
    }
  };

  if (!registrationData || !eventData) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-16 z-30 mt-16 border-b border-white/70 bg-white/85 backdrop-blur supports-[backdrop-filter]:bg-white/75 sm:top-20 sm:mt-20">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-full bg-gradient-to-r from-orange-500/[0.12] via-amber-500/[0.08] to-transparent" aria-hidden />
        <div className="relative mx-auto flex max-w-7xl flex-col gap-3 px-4 py-3 sm:gap-4 sm:px-6 sm:py-4 lg:flex-row lg:items-center lg:px-8">
          <div className="min-w-0 flex-1 text-left">
            <div className="mb-1 flex items-center justify-start">
              <span className="inline-flex items-center rounded-full border border-orange-200/70 bg-orange-500/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-orange-700">
                Payment
              </span>
            </div>
            <h1 className="text-base font-bold tracking-tight text-gray-900 sm:text-xl">
              Complete payment
            </h1>
            {eventData?.event_title && (
              <p className="mt-0.5 text-xs font-medium text-gray-600 sm:text-sm">
                {eventData.event_title}
              </p>
            )}
          </div>
          <div className="flex w-full shrink-0 items-center gap-2 lg:w-auto">
            {fromRegistrations && (
              <motion.button
                type="button"
                onClick={() => navigate("/conference-registrations")}
                whileHover={{ x: -2 }}
                whileTap={{ scale: 0.98 }}
                className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-gray-200/80 bg-white/90 px-3 py-2.5 text-sm font-semibold text-gray-700 shadow-sm transition-colors hover:bg-orange-500/10 hover:text-orange-700 sm:flex-none"
              >
                <ChevronLeft size={18} />
                Back to registrations
              </motion.button>
            )}
            <motion.button
              type="button"
              onClick={() =>
                navigate(`/event-details/${slug}/registration-success`, {
                  state: {
                    applicationId: slug,
                    registrationId: registrationData?.id || registrationData?.registration_id,
                    registrationData,
                    eventData,
                    paymentVerified: false,
                    payLater: true,
                    ...(isGroup && { isGroup: true, numberOfAttendees }),
                    ...(totalAmount != null && { totalAmount }),
                    ...(stateAttendees?.length > 0 && { attendees: stateAttendees }),
                    ...(paymentData.selectedTicket && { selectedTicket: paymentData.selectedTicket }),
                  },
                })
              }
              whileHover={{ x: 2 }}
              whileTap={{ scale: 0.98 }}
              className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-gray-200/80 bg-white/90 px-3 py-2.5 text-sm font-semibold text-gray-700 shadow-sm transition-colors hover:bg-orange-500/10 hover:text-orange-700 sm:flex-none"
            >
              Pay later
            </motion.button>
          </div>
        </div>
      </header>

      <div className="px-4 py-8 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-8xl">
          {/* Ticket Summary - show only the ticket being purchased */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="rounded-2xl border border-gray-200/90 bg-white p-5 shadow-sm sm:p-6">
              <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-gray-500 sm:mb-5">
                Ticket
              </h2>
              {eventDataLoading ? (
                <div className="flex items-center justify-center py-8 text-gray-500">
                  <span className="text-sm font-medium">Loading ticket details…</span>
                </div>
              ) : paymentData.selectedTicket ? (
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center justify-between gap-4 rounded-xl border-2 border-orange-500 bg-orange-50/80 px-4 py-4 sm:px-5 sm:py-5"
                >
                  <div className="min-w-0 flex-1 pr-8">
                    <span className="block text-xs font-semibold uppercase tracking-wide text-orange-700">
                      Ticket being purchased
                    </span>
                    <span className="block font-semibold text-gray-900 sm:text-lg">
                      {paymentData.selectedTicket.name}
                    </span>
                  </div>
                  <div className="shrink-0 text-right">
                    <span className="text-sm font-medium text-gray-500">GH₵</span>
                    <span className="text-2xl font-bold tabular-nums text-orange-600 sm:text-3xl">
                      {paymentData.selectedTicket.price}
                    </span>
                  </div>
                </motion.div>
              ) : (
                <div className="flex items-center justify-between rounded-xl border border-dashed border-orange-200 bg-orange-50/40 px-4 py-4 sm:px-5 sm:py-5">
                  <p className="text-sm text-gray-600">
                    Ticket details are not available. Please return to the event page and start the payment again.
                  </p>
                </div>
              )}
            </div>
          </motion.div>

          {/* Payment Details Section - Two Columns */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
            {/* Left: Payment Form */}
            <div className="lg:col-span-3">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="p-6 bg-white rounded-2xl border border-gray-200 shadow-lg"
              >
                <div className="flex gap-3 items-center pb-6 mb-6 border-b border-gray-100">
                  <div className="flex justify-center items-center w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg shadow-md">
                    <Smartphone className="text-white" size={20} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Payment Method</h3>
                    <p className="text-sm text-gray-600">Choose how you'd like to pay</p>
                  </div>
                </div>

                {/* Payment Method Selection */}
                <div className="mb-6">
                  <div className="grid grid-cols-2 gap-4">
                    <motion.button
                      type="button"
                      onClick={() => setPaymentMethod("momo")}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className={`relative overflow-hidden flex flex-col gap-2 items-center p-5 rounded-xl border-2 transition-all ${
                        paymentMethod === "momo"
                          ? "bg-gradient-to-br from-orange-50 to-orange-100/50 border-orange-500 shadow-lg ring-2 ring-orange-200"
                          : "bg-white border-gray-200 hover:border-orange-300 hover:shadow-md"
                      }`}
                    >
                      {paymentMethod === "momo" && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="flex absolute top-2 right-2 justify-center items-center w-6 h-6 bg-orange-500 rounded-full"
                        >
                          <Check size={14} className="text-white" />
                        </motion.div>
                      )}
                      <div className={`p-3 rounded-xl ${paymentMethod === "momo" ? "bg-orange-500" : "bg-gray-100"}`}>
                        <Smartphone size={24} className={paymentMethod === "momo" ? "text-white" : "text-gray-600"} />
                      </div>
                      <div className="text-center">
                        <div className={`text-sm font-bold ${paymentMethod === "momo" ? "text-orange-900" : "text-gray-700"}`}>
                          Mobile Money
                        </div>
                        <div className="text-xs text-gray-500">Ghana</div>
                      </div>
                    </motion.button>

                    <motion.button
                      type="button"
                      onClick={() => setPaymentMethod("card")}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className={`relative overflow-hidden flex flex-col gap-2 items-center p-5 rounded-xl border-2 transition-all ${
                        paymentMethod === "card"
                          ? "bg-gradient-to-br from-orange-50 to-orange-100/50 border-orange-500 shadow-lg ring-2 ring-orange-200"
                          : "bg-white border-gray-200 hover:border-orange-300 hover:shadow-md"
                      }`}
                    >
                      {paymentMethod === "card" && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="flex absolute top-2 right-2 justify-center items-center w-6 h-6 bg-orange-500 rounded-full"
                        >
                          <Check size={14} className="text-white" />
                        </motion.div>
                      )}
                      <div className={`p-3 rounded-xl ${paymentMethod === "card" ? "bg-orange-500" : "bg-gray-100"}`}>
                        <CreditCard size={24} className={paymentMethod === "card" ? "text-white" : "text-gray-600"} />
                      </div>
                      <div className="text-center">
                        <div className={`text-sm font-bold ${paymentMethod === "card" ? "text-orange-900" : "text-gray-700"}`}>
                          Card Payment
                        </div>
                        <div className="text-xs text-gray-500">Debit/Credit</div>
                      </div>
                    </motion.button>
                  </div>
                </div>

                {/* Continue to payment - no MoMo form; generic momo fields sent in background */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="mt-6"
                >
                  {isProcessing ? (
                    <div className="flex flex-col gap-4 justify-center items-center p-12 text-center bg-gradient-to-br from-orange-50 to-white rounded-xl border border-orange-100">
                      <div className="relative">
                        <div className="w-16 h-16 rounded-full border-4 border-orange-200 animate-spin border-t-orange-500" />
                        <div className="flex absolute inset-0 justify-center items-center">
                          <div className="w-8 h-8 bg-orange-500 rounded-full animate-pulse" />
                        </div>
                      </div>
                      <div>
                        <p className="mb-1 text-lg font-bold text-gray-900">Processing Payment</p>
                        <p className="text-sm text-gray-600">Please wait while we process your payment...</p>
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-6">
                      <p className="mb-4 text-sm text-gray-600">
                        {paymentMethod === "card"
                          ? "You will enter your card details securely on the payment provider's page after you continue."
                          : "You will complete Mobile Money payment on the next screen."}
                      </p>
                      <motion.button
                        type="button"
                        onClick={() => {
                          if (isProcessing) return;
                          if (!paymentData.selectedTicket) {
                            setErrors({ selectedTicket: "Please select a ticket" });
                            toast.error("Please select a ticket");
                            return;
                          }
                          setErrors({});
                          handlePayment(paymentData);
                        }}
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                        className="w-full rounded-xl bg-orange-500 px-4 py-3 text-base font-semibold text-white shadow-md transition-colors hover:bg-orange-600"
                      >
                        Continue to payment
                      </motion.button>
                    </div>
                  )}
                </motion.div>
              </motion.div>
            </div>

            {/* Right: Order Summary */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="sticky top-32 p-6 bg-gradient-to-br from-orange-50 via-white rounded-2xl border border-orange-100 shadow-lg to-orange-50/30"
              >
                <div className="flex gap-3 items-center pb-6 mb-6 border-b border-orange-200">
                  <div className="flex justify-center items-center w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg shadow-md">
                    <Shield className="text-white" size={20} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Order Summary</h3>
                </div>
                
                <div className="mb-6 space-y-4">
                  <div className="p-4 bg-white rounded-xl border border-orange-100">
                    <p className="mb-1 text-xs font-medium tracking-wide text-gray-500 uppercase">Application ID</p>
                    <p className="font-mono text-lg font-bold text-gray-900">
                      {slug}
                    </p>
                  </div>

                  {paymentData.selectedTicket ? (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="p-4 space-y-3 bg-white rounded-xl border border-orange-100"
                    >
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-600">Ticket Type</span>
                        <span className="text-sm font-bold text-gray-900">{paymentData.selectedTicket.name}</span>
                      </div>
                      {isGroup && numberOfAttendees != null && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-600">Attendees</span>
                          <span className="text-sm font-bold text-gray-900">{numberOfAttendees}</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center pt-3 border-t border-gray-100">
                        <span className="text-sm font-medium text-gray-600">{isGroup ? "Unit price" : "Price"}</span>
                        <span className="text-sm font-bold text-gray-900">
                          {paymentData.selectedTicket.price === 0 || parseFloat(paymentData.selectedTicket.price) === 0 ? "FREE" : `GH₵ ${paymentData.selectedTicket.price}`}
                        </span>
                      </div>
                    </motion.div>
                  ) : (
                    <div className="p-6 text-center bg-white rounded-xl border border-orange-200 border-dashed">
                      <p className="text-sm text-gray-500">No ticket selected</p>
                    </div>
                  )}
                </div>

                <div className="p-5 mb-6 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-white">Total Amount</span>
                    <span className="text-2xl font-bold text-white">
                      {isGroup && totalAmount != null
                        ? (totalAmount === 0 ? "FREE" : `GH₵ ${Number(totalAmount).toFixed(2)}`)
                        : (paymentData.selectedTicket?.price === 0 || parseFloat(paymentData.selectedTicket?.price) === 0 ? "FREE" : `GH₵ ${Number(paymentData.selectedTicket?.price ?? 0).toFixed(2)}`)}
                    </span>
                  </div>
                </div>

                <div className="p-4 space-y-3 bg-white rounded-xl border border-orange-100">
                  <div className="flex gap-3 items-start">
                    <div className="flex flex-shrink-0 justify-center items-center w-8 h-8 bg-green-100 rounded-lg">
                      <Shield size={16} className="text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Secure Payment</p>
                      <p className="text-xs text-gray-600">256-bit SSL encryption</p>
                    </div>
                  </div>
                  <div className="flex gap-3 items-start">
                    <div className="flex flex-shrink-0 justify-center items-center w-8 h-8 bg-blue-100 rounded-lg">
                      <Lock size={16} className="text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Data Protection</p>
                      <p className="text-xs text-gray-600">Your information is safe with us</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

